interface IgnoreMap {
    [key: string]: Set<string>;
}
interface QueueTask<T = unknown> {
    fn: () => Promise<T>;
    taskId: string;
    resolve: (value: T | null) => void;
    reject: (reason?: unknown) => void;
    ignored: boolean;
}

const QUEUE_SIZE = 100;

// Очередь с игнорируемыми задачами, с помощью этой утилиты можно заставить функции возвращающие промис выполняться последовательно
// Помимо обычных последовательных вызовов присутствует функция игнорирования задач, которая позволяет игнорировать задачи,
// которые ещё не запустились, заигнорированные задачи не будут выполняться, а промис вернёт null
// -
// IgnoreMap - мапа игнорируемых задач, где ключ - идентификатор задачи, а значение - массив идентификаторов задач, которые должны быть игнорированы,
// в том числе в массив можно положить задачу равную ключу, чтобы получить leading throttling без таймаута
// Задача игнорируются только если мы знаем, что в будущем будет выполняться задача, которая игнорирует текущую исходя из правил IgnoreMap
// -
// Для того чтобы очередь понимала какие задачи должны быть игнорированы, для каждой задачи нужно указать идентификатор во время запуска (runTask)
// Один идентификатор должен обозначать одну функцию (одинаковый для вызовов одной конкретной функции)

class QueueWithIgnores {
    private queue: QueueTask<unknown>[] = [];
    private ignoreMap: IgnoreMap;
    private executing = false;
    private head = 0;
    private tasksMap = new Map<string, Set<QueueTask<unknown>>>();
    constructor(ignoreMap: IgnoreMap) {
        this.ignoreMap = ignoreMap;
    }

    private cleanQueue(): void {
        this.queue.splice(0, this.head);
        this.head = 0;
    }

    public runTask<T>(fn: () => Promise<T>, taskId: string): Promise<T | null> {
        if (taskId in this.ignoreMap && this.queue.length > 0) {
            const ignores = this.ignoreMap[taskId];
            for (const needIgnoreTask of ignores) {
                const tasksIgnore = this.tasksMap.get(needIgnoreTask);
                if (!tasksIgnore) continue;
                for (const task of tasksIgnore) {
                    if (!task.ignored) {
                        task.ignored = true;
                    }
                }
                this.tasksMap.delete(needIgnoreTask);
            }
        }

        return new Promise<T | null>((resolve, reject) => {
            if (!this.tasksMap.has(taskId)) {
                this.tasksMap.set(taskId, new Set());
            }
            const newTask = {
                fn,
                taskId,
                resolve: resolve as (value: unknown) => void,
                reject,
                ignored: false,
            };
            this.tasksMap.get(taskId)?.add(newTask);
            this.queue.push(newTask);
            this.runQueue();
        });
    }

    // Основной цикл очереди
    private async runQueue(): Promise<void> {
        if (this.executing) return;
        this.executing = true;

        while (this.head < this.queue.length) {
            const task = this.queue[this.head];

            try {
                if (!task.ignored) {
                    const result = await task.fn();
                    task.resolve(result);
                } else {
                    task.resolve(null);
                }
            } catch (error: unknown) {
                task.reject(error);
            } finally {
                const currentTasks = this.tasksMap.get(task.taskId);
                currentTasks?.delete(task);
                if (currentTasks?.size === 0) {
                    this.tasksMap.delete(task.taskId);
                }
            }
            this.head++;
            if (this.head > QUEUE_SIZE) {
                this.cleanQueue();
            }
        }
        this.cleanQueue();
        this.executing = false;
    }
}

export { QueueWithIgnores };
